/* eslint-disable no-return-await */
import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import getIsRoktEnabledForCheckoutPage from '../../v2/selectors/RoktSelectors/getIsRoktEnabledForCheckoutPage';
import getIsRoktSandboxEnabledForCheckoutPage from '../../v2/selectors/RoktSelectors/getIsRoktSandboxEnabledForCheckoutPage';
import getPrivacyConsentFromPrivacyGroup from '../../v2/selectors/RoktSelectors/getPrivacyConsentFromPrivacyGroup';
import getRoktIdForCheckoutPage from '../../v2/selectors/RoktSelectors/getRoktIdForCheckoutPage';
import DomUtils from '../../v2/utils/DomUtils';
import Track from '../../v2/utils/tracking';
import doMetric from '../../v2/tracking/bigQueryMetricRecorder';
import {
  getCurrency, getLocaleFromSiteDataV2, getOrderTotal, getStart,
} from '../../v2/selectors/selectors';

export function RoktCheckoutPage(props) {
  const loadedRokt = useRef(false);
  
  const enableRoktJS = async () => {
    await new Promise((resolve) => {
      if (window.Rokt) {
        resolve();
      } else {
        document.getElementById('rokt-launcher').onload = resolve;
      }
      
      return window.Rokt;
    });
  };
  
  const createLauncher = async () => await window.Rokt.createLauncher({
    accountId: props.roktId,
    sandbox: props.sandbox,
    noFunctional: !props.consentedToTargetingPrivacyConsentGroups,
    noTargeting: !props.consentedToTargetingPrivacyConsentGroups,
  });
  
  const attributes = {
    amount: props.amount,
    country: props.country,
    currency: props.currency,
    language: props.language,
    emailEnc: props.emailEnc,
    cartItems: props.cartItems,
  };
  
  const getSelection = async launcher => await launcher.selectPlacements({
    identifier: 'payments',
    attributes,
  });
  
  useEffect(() => {
    if (!loadedRokt.current) {
      DomUtils.addScript(
        'https://apps.rokt.com/wsdk/integrations/launcher.js',
        true,
        '',
        'high',
        true,
        'rokt-launcher',
      ).then(() => {
        loadedRokt.current = true;
        
        try {
          doMetric('Rokt', [{name: 'loaded', value: true}], [], 'impression');
        } catch (error) {
          Track.track(error);
        }
        
        enableRoktJS().then(() => {
          createLauncher().then((launcher) => {
            getSelection(launcher).then(((selection) => {
              selection.on('POSITIVE_ENGAGEMENT').subscribe(() => {
                selection.setAttributes({...attributes});
              });
            }));
          });
        }).catch(e => Track.error(`Error using ROKT script. ${e}`));
      }).catch(e => Track.error(`Error laoding ROKT script. ${e}`));
    }
  }, [props.roktCheckoutEnabled, props.roktId, loadedRokt, props.amount, props.currency, props.country,
    props.language, props.emailEnc]);
  
  return <div id="rokt-placeholder" data-cs-mask />;
}

RoktCheckoutPage.propTypes = {
  roktCheckoutEnabled: PropTypes.bool,
  roktId: PropTypes.string,
  sandbox: PropTypes.bool,
  amount: PropTypes.number,
  country: PropTypes.string,
  currency: PropTypes.string,
  language: PropTypes.string,
  emailEnc: PropTypes.string,
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.number,
      productName: PropTypes.string,
      majorCat: PropTypes.string,
    }),
  ),
};

const mapStateToProps = (state) => {
  const start = getStart(state);
  const country = start?.customer?.location;
  const emailEnc = start?.encryptedValues?.emailEnc;
  const cartItems = start?.order?.basketItems.map(item => ({
    sku: item?.productId,
    productName: item?.productTitle,
    majorCat: item?.productTypeName,
  }));
  
  return {
    roktCheckoutEnabled: getIsRoktEnabledForCheckoutPage(state),
    roktId: getRoktIdForCheckoutPage(state),
    sandbox: getIsRoktSandboxEnabledForCheckoutPage(state),
    amount: getOrderTotal(state),
    country,
    currency: getCurrency(state),
    language: getLocaleFromSiteDataV2(state),
    emailEnc,
    cartItems,
    consentedToTargetingPrivacyConsentGroups: !!getPrivacyConsentFromPrivacyGroup(state)
  };
};

export default connect(mapStateToProps)(RoktCheckoutPage);
